// Mark-held workflow modal. Captures the heldDate (defaults to today)
// and an optional minutes URL. Validates the URL before submitting and
// dedupes against the meeting's existing evidenceLinks by URL — the
// dialog is the source of truth for the merged evidenceLinks array so
// the parent never has to filter duplicates. A soft warning surfaces
// when the heldDate is more than 365 days before today; non-blocking,
// purely an "are you sure?" nudge for the typo case.

import { useMemo, useState } from "react";
import Modal from "../../../common/Modal";
import Button from "../../../common/Button";
import type { EvidenceLink } from "../../../../types";

export interface MarkHeldResult {
  heldDate: string;
  minutesLink: string | undefined;
  evidenceLinks: EvidenceLink[];
}

export interface MarkHeldDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (result: MarkHeldResult) => void | Promise<unknown>;
  meetingName: string;
  currentEvidenceLinks: EvidenceLink[];
  /** Inject for deterministic testing; defaults to `new Date()`. */
  today?: Date;
}

const MS_PER_DAY = 86_400_000;
const ONE_YEAR_DAYS = 365;

function todayIsoDate(today: Date): string {
  return today.toISOString().slice(0, 10);
}

function isValidUrl(s: string): boolean {
  try {
    const u = new URL(s);
    return u.protocol === "http:" || u.protocol === "https:";
  } catch {
    return false;
  }
}

function daysAgoUtc(iso: string, today: Date): number {
  const a = new Date(`${iso}T00:00:00Z`);
  if (Number.isNaN(a.getTime())) return 0;
  const tod = Date.UTC(
    today.getUTCFullYear(),
    today.getUTCMonth(),
    today.getUTCDate(),
  );
  return Math.round((tod - a.getTime()) / MS_PER_DAY);
}

function dedupeEvidenceLinks(links: EvidenceLink[]): EvidenceLink[] {
  const seen = new Set<string>();
  const out: EvidenceLink[] = [];
  for (const l of links) {
    if (seen.has(l.url)) continue;
    seen.add(l.url);
    out.push(l);
  }
  return out;
}

export default function MarkHeldDialog({
  isOpen,
  onClose,
  onConfirm,
  meetingName,
  currentEvidenceLinks,
  today = new Date(),
}: MarkHeldDialogProps) {
  const [heldDate, setHeldDate] = useState(() => todayIsoDate(today));
  const [minutesLink, setMinutesLink] = useState("");
  const [errors, setErrors] = useState<{ heldDate?: string; minutesLink?: string }>(
    {},
  );

  // Pattern 0: reset fields to today's date when the dialog opens so a
  // previous (possibly cancelled) workflow does not persist.
  const [seededFor, setSeededFor] = useState(false);
  if (isOpen && !seededFor) {
    setSeededFor(true);
    setHeldDate(todayIsoDate(today));
    setMinutesLink("");
    setErrors({});
  }
  if (!isOpen && seededFor) {
    setSeededFor(false);
  }

  const isFarPast = useMemo(() => {
    if (!heldDate) return false;
    return daysAgoUtc(heldDate, today) > ONE_YEAR_DAYS;
  }, [heldDate, today]);

  if (!isOpen) return null;

  const handleConfirm = async () => {
    const next: typeof errors = {};
    if (!heldDate) next.heldDate = "Held date is required.";
    const trimmedUrl = minutesLink.trim();
    if (trimmedUrl && !isValidUrl(trimmedUrl)) {
      next.minutesLink = "Enter a valid URL (http or https).";
    }
    if (Object.keys(next).length > 0) {
      setErrors(next);
      return;
    }
    const merged = trimmedUrl
      ? dedupeEvidenceLinks([
          ...currentEvidenceLinks,
          { label: "Minutes", url: trimmedUrl },
        ])
      : [...currentEvidenceLinks];
    await onConfirm({
      heldDate,
      minutesLink: trimmedUrl || undefined,
      evidenceLinks: merged,
    });
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Mark held">
      <div className="space-y-4">
        <p className="text-body-sm text-slate-600 dark:text-slate-300">
          Confirm when {meetingName} was held. You can attach a minutes link
          now or add it later.
        </p>
        <div>
          <label htmlFor="markheld-date" className="label">
            Held date <span className="text-red-600">*</span>
          </label>
          <input
            id="markheld-date"
            type="date"
            className="input"
            value={heldDate}
            onChange={(e) => setHeldDate(e.target.value)}
            aria-invalid={!!errors.heldDate}
          />
          {errors.heldDate && (
            <p className="mt-1 text-caption text-red-600 dark:text-red-400">
              {errors.heldDate}
            </p>
          )}
          {isFarPast && !errors.heldDate && (
            <p className="mt-1 text-caption text-amber-700 dark:text-amber-400">
              This date is more than a year ago — is that correct?
            </p>
          )}
        </div>
        <div>
          <label htmlFor="markheld-minutes" className="label">
            Minutes link
          </label>
          <input
            id="markheld-minutes"
            type="url"
            className="input"
            placeholder="https://…"
            value={minutesLink}
            onChange={(e) => setMinutesLink(e.target.value)}
            aria-invalid={!!errors.minutesLink}
          />
          {errors.minutesLink && (
            <p className="mt-1 text-caption text-red-600 dark:text-red-400">
              {errors.minutesLink}
            </p>
          )}
        </div>
        <div className="flex justify-end gap-3 pt-2">
          <Button variant="secondary" type="button" onClick={onClose}>
            Cancel
          </Button>
          <Button type="button" onClick={handleConfirm}>
            Mark held
          </Button>
        </div>
      </div>
    </Modal>
  );
}
